import { useCallback, useState } from "react";

import {
  SubscriptionPlans,
  SubscriptionProvider,
  SubscriptionStatus,
} from "@prisma/client";

import Button from "@musicfy/components/Button";
import Divider from "@musicfy/components/Divider";
import CancelSubscriptionButton from "@musicfy/components/PaymentButtons/CancelSubscriptionButton";
import StartCheckoutFlowButton from "@musicfy/components/PaymentButtons/StartCheckoutFlowButton";
import { usePaddleUpdatePayment } from "@musicfy/libs/PaddleProvider/hooks";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import {
  api,
  canCancelSubscription,
  canSelfServeChangeSubscription,
  getSubscriptionChangeBlockReason,
  getSubscriptionChangeMessage,
  SubscriptionChangeBlockReason,
  formatPrice,
  getLocale,
  parsePaddleAmount,
} from "@musicfy/utils";

import { SettingsPanel, type TSettingsPanel } from "../../types";

interface SubscriptionDetailsProps {
  setActivePanel: (panel: TSettingsPanel) => void;
}

const SubscriptionDetails = ({
  setActivePanel,
}: SubscriptionDetailsProps): JSX.Element => {
  const {
    subscription,
    setSubscription,
    renewSubscription,
    createStripePortalSession,
    paddleSubscription,
    isPaddleLoading,
  } = useSubscriptionContext();
  const { sendToastMessage } = useToastContext();
  const { startUpdatePayment } = usePaddleUpdatePayment();
  const [isUndoingCancellation, setIsUndoingCancellation] = useState(false);
  const cancelPendingDowngrade =
    api.subscription.cancelPendingStripeDowngrade.useMutation({
      onSuccess: (data) => {
        setSubscription(data);
        sendToastMessage("Your scheduled downgrade has been canceled.", {
          severity: "success",
        });
      },
      onError: (error) => {
        sendToastMessage(error.message, { severity: "error" });
      },
    });

  const handleUndoCancellation = useCallback(async () => {
    if (!subscription || isUndoingCancellation) {
      return;
    }
    setIsUndoingCancellation(true);
    try {
      await renewSubscription();
      sendToastMessage("Your subscription will continue as usual.", {
        severity: "success",
      });
    } catch (error) {
      const message =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message: unknown }).message === "string"
          ? (error as { message: string }).message
          : "Unable to keep your subscription. Please try again.";
      sendToastMessage(message, { severity: "error" });
    } finally {
      setIsUndoingCancellation(false);
    }
  }, [
    isUndoingCancellation,
    renewSubscription,
    sendToastMessage,
    subscription,
  ]);

  if (!subscription || subscription.plan === SubscriptionPlans.free) {
    return (
      <div className="flex flex-col xl:flex-row lg:items-start justify-between gap-4">
        <div className="text-20 text-gray-500 whitespace-nowrap w-1/3">
          Subscription Type
        </div>
        <div className="w-full flex flex-col justify-between">
          <div className="flex items-center">
            <div>
              <div className="uppercase text-12 font-bold tracking-wide text-gray-500">
                Current Plan
              </div>
              <div className="mt-2 text-22 capitalize">
                {SubscriptionPlans.free}
              </div>
            </div>
          </div>
          <StartCheckoutFlowButton
            origin="account-settings"
            className="!rounded-full !px-12 mt-6 lg:ml-auto"
          />
        </div>
      </div>
    );
  }

  const locale = getLocale();

  const isCancelling = !!subscription.canceledAt;
  const isPaused = subscription.status === SubscriptionStatus.paused;

  const paddleLoadFailed =
    subscription.provider === "paddle" &&
    !subscription.subscriptionId.includes("temp") &&
    !isPaddleLoading &&
    !paddleSubscription;

  const changeBlockReason = getSubscriptionChangeBlockReason(subscription, {
    paddleLoadFailed,
    requireNotCanceling: true,
  });
  const canChangePlan =
    !subscription.pendingPlan &&
    canSelfServeChangeSubscription(subscription, {
      paddleLoadFailed,
      requireNotCanceling: true,
    });
  const showCancelSubscription =
    subscription.status !== SubscriptionStatus.canceled &&
    canCancelSubscription(subscription);

  let nextPaymentDate = subscription.periodEnd
    ? new Date(subscription.periodEnd).toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  if (!subscription.periodEnd && isPaused) {
    nextPaymentDate = subscription.periodStart
      ? new Date(subscription.periodStart).toLocaleDateString(locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;
  }

  const nextTransactionTotals =
    paddleSubscription?.next_transaction?.details.totals;
  const currencyCode = nextTransactionTotals?.currency_code ?? "USD";

  const grandTotal = !!nextTransactionTotals
    ? formatPrice(
        parsePaddleAmount(nextTransactionTotals.grand_total, currencyCode),
        currencyCode
      )
    : null;

  let paymentTitleText = "Upcoming Payment";

  if (isCancelling) {
    paymentTitleText = "Cancelling On";
  } else if (isPaused) {
    paymentTitleText = "Paused Until";
  }

  const renderChangeSubscriptionAction = (className: string) => {
    if (canChangePlan) {
      return (
        <StartCheckoutFlowButton
          origin="account-settings"
          className={className}
        >
          Change Subscription
        </StartCheckoutFlowButton>
      );
    }

    if (changeBlockReason === SubscriptionChangeBlockReason.tempOrNonBillable) {
      return (
        <StartCheckoutFlowButton
          origin="account-settings-setup-billing"
          className={className}
        >
          Set up billing
        </StartCheckoutFlowButton>
      );
    }

    if (changeBlockReason === SubscriptionChangeBlockReason.stripe) {
      return (
        <Button
          onClick={() => createStripePortalSession()}
          className={className}
          variant="outlined"
        >
          Manage in Stripe
        </Button>
      );
    }

    if (
      changeBlockReason ===
        SubscriptionChangeBlockReason.paymentMethodRequired ||
      changeBlockReason === SubscriptionChangeBlockReason.paddleUnavailable
    ) {
      return (
        <Button
          onClick={startUpdatePayment}
          className={className}
          variant="outlined"
        >
          Update Payment Method
        </Button>
      );
    }

    if (
      changeBlockReason === SubscriptionChangeBlockReason.invalidStatus &&
      subscription.status === SubscriptionStatus.past_due
    ) {
      const onUpdatePayment =
        subscription.provider === SubscriptionProvider.stripe
          ? () => createStripePortalSession()
          : () => {
              void startUpdatePayment();
            };

      return (
        <Button
          onClick={onUpdatePayment}
          className={className}
          variant="outlined"
        >
          Update Payment Method
        </Button>
      );
    }

    if (
      changeBlockReason === SubscriptionChangeBlockReason.invalidStatus &&
      subscription.status === SubscriptionStatus.canceled
    ) {
      return (
        <StartCheckoutFlowButton
          origin="account-settings-resubscribe"
          className={className}
        >
          Choose a New Plan
        </StartCheckoutFlowButton>
      );
    }

    return null;
  };

  // Pending cancel/downgrade banners carry the action; skip the plain note.
  const changeBlockedNote =
    subscription.pendingPlan || isCancelling
      ? null
      : !canChangePlan && changeBlockReason
        ? getSubscriptionChangeMessage(changeBlockReason)
        : null;

  const cancellationDate =
    nextPaymentDate ??
    (subscription.canceledAt
      ? new Date(subscription.canceledAt).toLocaleDateString(locale, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null);

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="xl:text-20 text-16 text-gray-500 whitespace-nowrap xl:w-1/3">
          Subscription Type
        </div>
        <div className="w-full xl:flex hidden items-center justify-between">
          <div className="flex items-center gap-9">
            <div>
              <div className="uppercase text-10 font-bold tracking-wide text-gray-500">
                Current Plan
              </div>
              <div className="mt-2 text-18 capitalize">{subscription.plan}</div>
            </div>

            <div>
              <div className="uppercase text-10 font-bold tracking-wide text-gray-500">
                Billing Cycle
              </div>
              <div className="mt-2 text-18 capitalize">
                {subscription.interval}
              </div>
            </div>

            <div>
              <div className="uppercase text-10 font-bold tracking-wide text-gray-500">
                Status
              </div>
              <div className="mt-2 text-18 capitalize">
                {subscription.status.replace("_", " ")}
              </div>
            </div>
          </div>

          {renderChangeSubscriptionAction(
            "!rounded-full !px-12 !min-h-fit !py-2"
          )}
        </div>

        <div className="xl:hidden text-16 text-right capitalize font-light">
          {subscription.plan} Plan ({subscription.interval}) ·{" "}
          {subscription.status.replace("_", " ")}
        </div>
      </div>

      {!!changeBlockedNote && (
        <div className="xl:ml-[33.333%] text-14 text-gray-400 -mt-2">
          {changeBlockedNote}
        </div>
      )}

      {isCancelling && (
        <div className="xl:ml-[33.333%] rounded-12 border border-white/10 bg-white/5 p-4">
          <div className="text-14 text-white/80">
            Your subscription is set to cancel on{" "}
            <span className="font-bold">{cancellationDate}</span>. You keep
            access until then. Renew to keep your plan and change it later.
          </div>
          <Button
            variant="text"
            className="mt-2 !min-h-fit !p-0"
            loading={isUndoingCancellation}
            onClick={() => {
              void handleUndoCancellation();
            }}
          >
            Keep Subscription
          </Button>
        </div>
      )}

      {!!subscription.pendingPlan && !isCancelling && (
        <div className="xl:ml-[33.333%] rounded-12 border border-white/10 bg-white/5 p-4">
          <div className="text-14 text-white/80">
            Downgrade to{" "}
            <span className="font-bold capitalize">
              {subscription.pendingPlan}
            </span>{" "}
            ({subscription.pendingInterval}) scheduled for{" "}
            {subscription.pendingChangeAt
              ? new Date(subscription.pendingChangeAt).toLocaleDateString(
                  locale,
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )
              : "your next billing date"}
            . You keep your current plan until then.
          </div>
          <Button
            variant="text"
            className="mt-2 !min-h-fit !p-0"
            loading={cancelPendingDowngrade.isLoading}
            onClick={() =>
              cancelPendingDowngrade.mutate({
                subscriptionId: subscription.subscriptionId,
              })
            }
          >
            Undo Scheduled Downgrade
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="xl:text-20 text-16 text-gray-500 whitespace-nowrap xl:w-1/3">
          {paymentTitleText}
        </div>
        <div className="w-full xl:flex hidden items-center justify-between">
          <div className="flex items-center gap-7 uppercase">
            <div>
              <div className="uppercase text-10 font-bold tracking-wide text-gray-500">
                Date
              </div>
              <div className="mt-2 text-18 capitalize">{nextPaymentDate}</div>
            </div>
            {!!grandTotal && !isCancelling && !isPaused && (
              <div>
                <div className="uppercase text-10 font-bold tracking-wide text-gray-500">
                  Amount
                </div>
                <div className="mt-2 text-18 capitalize">{grandTotal}</div>
              </div>
            )}
            {subscription.provider === "paddle" && (
              <Button
                onClick={() => setActivePanel(SettingsPanel.payments)}
                className="!rounded-full !text-14 !min-h-fit !py-2"
                variant="outlined"
              >
                View Payment History
              </Button>
            )}
          </div>
          {subscription.provider === "paddle" && canChangePlan && (
            <Button
              onClick={startUpdatePayment}
              className="!rounded-full !text-14 !min-h-fit !py-2"
              variant="text"
            >
              Update Payment Method
            </Button>
          )}
        </div>
        <div className="xl:hidden text-right">
          <div className=" text-16">{nextPaymentDate}</div>
          {!!grandTotal && !isCancelling && !isPaused && (
            <div className="mt-2 text-18 capitalize">{grandTotal}</div>
          )}
        </div>
      </div>

      <div className="hidden xl:flex w-fit ml-auto items-center gap-4">
        {subscription.provider === "stripe" && (
          <Button variant="text" onClick={() => createStripePortalSession()}>
            Payment Method &amp; Invoices
          </Button>
        )}
        {showCancelSubscription && <CancelSubscriptionButton />}
      </div>

      <div className="xl:hidden flex flex-col items-center gap-4 mx-auto w-full pb-4">
        {renderChangeSubscriptionAction(
          "!rounded-full !w-3/4 !px-2 !min-h-fit !py-2"
        )}
        {subscription.provider === "paddle" && (
          <Button
            onClick={() => setActivePanel(SettingsPanel.payments)}
            className="!rounded-full !w-3/4 !min-h-fit !py-2"
            variant="outlined"
          >
            View Payment History
          </Button>
        )}
        {subscription.provider === "paddle" && canChangePlan && (
          <Button
            onClick={startUpdatePayment}
            className="!rounded-full !text-14 !min-h-fit !py-2"
            variant="text"
          >
            Update Payment Method
          </Button>
        )}
        <Divider className="my-3" />
        {subscription.provider === "stripe" && (
          <Button variant="text" onClick={() => createStripePortalSession()}>
            Payment Method &amp; Invoices
          </Button>
        )}
        {showCancelSubscription && (
          <CancelSubscriptionButton className="!min-h-fit !py-1 relative before:content-[''] before:absolute before:bottom-0 before:bg-white before:w-full before:h-px" />
        )}
      </div>
    </>
  );
};

export default SubscriptionDetails;
